var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
var lastGeneratedId = 0;
function nextGeneratedId() {
    return 'awsui-textarea-' + lastGeneratedId++;
}
export var Textarea = AwsUi.__addComponent('awsui-textarea', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            if (!this.controlId) {
                this.controlId = nextGeneratedId();
            }
        }
    },
    properties: {
        // Text entered into the element.
        value: {
            type: 'string'
        },
        // Placeholder text rendered when the value is an empty string.
        placeholder: {
            type: 'string'
        },
        // The name of the control used in HTML forms.
        name: {
            type: 'string'
        },
        // Specifies that the textarea should be disabled, preventing the user from
        // modifying the value and preventing the value from being included in a
        // form submission. A disabled textarea cannot receive focus.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // Specifies that the textarea should be readonly, preventing the user from
        // modifying the value but including it in a form submission. A readonly textarea can receive focus.
        //
        // Do not use readonly textareas outside of a form.
        readonly: {
            type: 'boolean'
        },
        // Overrides invalidation state. Usually invalid state comes from wrapping `awsui-form-field` component, however
        // sometimes you need to override its state when you have more than one input within single form-field.
        invalid: {
            type: 'boolean',
            nullable: true
        },
        // Setting this to `true` will disable any native browser capabilities to
        // automatically correct user input, such as `autocorrect` and `autocapitalize`.
        // If unset, behavior will be the default behavior of the respective browser.
        disableBrowserAutocorrect: {
            type: 'boolean',
            defaultValue: false
        },
        // Allows you to indicate that the control is to be focused as
        // soon as the `load` event triggers, allowing the user to just start
        // typing without having to manually focus the input. Don't use this option
        // in pages that allow for the field to be scrolled out of the viewport.
        autofocus: {
            type: 'boolean'
        },
        // Id of the internal textarea. Useful for relating a label element's
        // "for" attribute to this control. It defaults to an automatically generated id.
        controlId: {
            type: 'string',
            announceValue: true
        },
        // The number of lines of text to set the height to.
        rows: {
            type: 'integer',
            defaultValue: 3
        },
        // Adds an aria label on the native textarea.
        label: {
            type: 'string'
        },
        // Adds aria-describedby on the native textarea. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string',
            nullable: true
        },
        // A default suggested aria-describedby value that can be pushed down from a FormField
        __suggestedAriaDescribedby: {
            type: 'string'
        },
        // Adds aria-required on the native input
        ariaRequired: {
            type: 'boolean'
        }
    },
    events: {
        // Fired when textarea focus is removed from the UI control.
        blur: {
            bubbles: false,
            cancelable: false
        },
        // Mimics the native `change` event on inputs.
        // The event `detail` contains the current value of the field.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'Textarea.ChangeDetail'
        },
        // Fires when input focus is set onto the UI control.
        focus: {
            bubbles: false,
            cancelable: false
        },
        // Mimics the native `input` event on inputs.
        // The event `detail` contains the current value of the field.
        input: {
            bubbles: true,
            cancelable: false,
            detailType: 'Textarea.ChangeDetail'
        },
        // Fired when the underlying native input fires `keydown` event.
        // The event `detail` contains the `keyCode` and information about modifiers (alt, ctrl, ...).
        keydown: {
            bubbles: true,
            cancelable: true,
            detailType: 'Textarea.KeyDetail'
        },
        // Fired when the underlying native input fires `keyup` event.
        // The event `detail` contains the `keyCode` and information about modifiers (alt, ctrl, ...).
        keyup: {
            bubbles: true,
            cancelable: true,
            detailType: 'Textarea.KeyDetail'
        },
        // Fired when the underlying native input fires `keypress` event.
        // The event `detail` contains the `key` property and information about modifiers (alt, ctrl, ...).
        keypress: {
            bubbles: true,
            cancelable: true,
            detailType: 'Textarea.KeyDetail'
        }
    },
    functions: {
        // Sets input focus onto the UI control.
        focus: function () {
            this.node.querySelector('.awsui-textarea').focus();
        }
    },
    render: function (m, h) {
        var _this = this;
        var keyHandler = function (eventName) {
            return function (event) {
                var keyCode = event.keyCode, key = event.key, ctrlKey = event.ctrlKey, shiftKey = event.shiftKey, altKey = event.altKey, metaKey = event.metaKey;
                _this.__fireEvent(eventName, { keyCode: keyCode, key: key, ctrlKey: ctrlKey, shiftKey: shiftKey, altKey: altKey, metaKey: metaKey }, {
                    nativeEvent: event
                });
            };
        };
        var inputHandler = function (event) {
            if (event.target.value !== _this.value) {
                _this.value = event.target.value;
                return _this.__fireEvent('input', { value: _this.value });
            }
        };
        var changeHandler = function () { return _this.__fireEvent('change', { value: _this.value }); };
        var cb = new LegacyClassBuilder(this);
        cb.addClass('awsui-textarea').addComponentClass('readonly', this.readonly);
        if (this.invalid !== null) {
            cb.addComponentClass('invalid', this.invalid);
            cb.addComponentClass('valid', !this.invalid);
        }
        var attributes = {
            className: cb.toClassName(),
            disabled: this.disabled,
            readonly: this.readonly,
            autofocus: this.autofocus,
            onblur: function () { return _this.__fireEvent('blur'); },
            onfocus: function () { return _this.__fireEvent('focus'); },
            onkeydown: keyHandler('keydown'),
            onkeyup: keyHandler('keyup'),
            onkeypress: keyHandler('keypress'),
            oninput: inputHandler,
            onchange: changeHandler,
            rows: this.rows,
            'aria-required': this.ariaRequired && 'true'
        };
        if (this.name) {
            attributes.name = this.name;
        }
        if (this.placeholder) {
            attributes.placeholder = this.placeholder;
        }
        if (this.label) {
            attributes['aria-label'] = this.label;
        }
        if (this.invalid) {
            attributes['aria-invalid'] = 'true';
        }
        if (this.disableBrowserAutocorrect) {
            attributes['autocorrect'] = 'off';
            attributes['autocapitalize'] = 'off';
        }
        // The 'value' attribute is handled separately for two reasons:
        // 1) It causes a rendering bug with Mitrhil when a character is entered
        //    in the input element. See the tests for more info.
        // 2) If the value is updated by the user, in IE the caret is reset to the end
        //    of the textarea, even if the update was in the middle of the text.
        // We work around these issues by having a config callback that only
        // changes the value if it has been updated.
        var initValue = function (element) {
            var textarea = element;
            if (textarea.value !== _this.value) {
                textarea.value = _this.value || '';
            }
        };
        attributes.lifecycleKey = 'textarea';
        attributes.componentDidMount = initValue;
        attributes.componentDidUpdate = initValue;
        if (this.controlId) {
            attributes.id = this.controlId;
        }
        var ariaDescribedby = this.ariaDescribedby || this.__suggestedAriaDescribedby;
        if (ariaDescribedby) {
            attributes['aria-describedby'] = ariaDescribedby;
        }
        return AwsUi.__createElement("textarea", __assign({}, attributes));
    },
    wrapperSupport: {
        ngModel: {
            value: 'value',
            event: 'input'
        }
    }
});
//# sourceMappingURL=index.js.map